import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '@hooks/useAnalytics/useAnalytics';
import { ROUTES } from '@constants/routes';
import type {
  CheckoutSource,
  UseProductOffersCheckoutArgs,
  UseProductOffersCheckoutReturn,
} from './useProductOffersCheckout.types';

/**
 * Owns the ProductOffersPage checkout action: fires the cart/checkout analytics,
 * drives the navigation mutation, and routes a failed attempt back to the
 * footer or drawer error banner depending on where checkout was triggered.
 */
export const useProductOffersCheckout = ({
  sessionId,
  cart,
  qualification,
  goToStep,
  onCloseCart,
  setFooterCheckoutError,
  setDrawerCheckoutError,
}: UseProductOffersCheckoutArgs): UseProductOffersCheckoutReturn => {
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const showCheckoutError = (source: CheckoutSource) => {
    const setCheckoutError = {
      footer: setFooterCheckoutError,
      drawer: setDrawerCheckoutError,
    };

    setCheckoutError[source](true);
  };

  const handleCheckout = async (source: CheckoutSource) => {
    setFooterCheckoutError(false);
    setDrawerCheckoutError(false);
    if (!cart) return;
    if (sessionId) {
      const cartAndQualification = { sessionId, cart, qualification };
      analytics.fire('trackViewCartInfo', cartAndQualification);
      analytics.fire('trackCheckoutInitiated', cartAndQualification);
    }
    try {
      const result = await goToStep('CHECKOUT');
      if (result.valid) {
        onCloseCart();
        navigate(ROUTES.CHECKOUT);
      } else {
        showCheckoutError(source);
      }
    } catch {
      showCheckoutError(source);
    }
  };

  return { handleCheckout };
};
